/* eslint-disable no-undef */
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "swiper/css";
import "swiper/css/pagination";
import "leaflet/dist/leaflet.css";

const Login = React.lazy(() => import("./components/Auth/Login"));
const MUALogin = React.lazy(() => import("./components/Auth/MUA"));
const Register = React.lazy(() => import("./components/Auth/Register"));
const CEmail = React.lazy(() => import("./components/Auth/CEmail"));
const ForgetPass = React.lazy(() => import("./components/Auth/ForgetPass"));
const Lost = React.lazy(() => import("./components/Lost"));
const AddDevice = React.lazy(() => import("./components/AddDevice"));
const Account = React.lazy(() => import("./components/Account"));
const General = React.lazy(() => import("./components/Account/Tabs/General"));
const Billing = React.lazy(() => import("./components/Account/Tabs/Billing"));
const Referrals = React.lazy(() => import("./components/Referrals"));
const Success = React.lazy(() => import("./components/Success"));
const MyDevices = React.lazy(() => import("./components/MyDevices"));
const Redirect = React.lazy(() => import("./components/Redirect"));
const FourZeroFour = React.lazy(() => import("./components/FourZeroFour"));
const Share = React.lazy(() => import("./components/Share"));
const WelcomeBox = React.lazy(() => import("./components/welcomebox"));

/*
const Track = React.lazy(() => import("./components/Track"));
const History = React.lazy(() => import("./components/History"));
*/

import getLocale from "./utils/locale";
import Navbar from "./components/Navbar";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from "react-query";
import ScrollToTop from "./hooks/ScrollToTop";
import ErrorFallback from "./components/ErrorFallback";
import { ErrorBoundary } from "react-error-boundary";
import { AuthContext } from "./Context/AuthContext";
import { redirects } from "./utils/redirects";
import { loadFreshworks } from "./freshworks";
import { initSentry } from "./sentry";

const Lazy = ({ children }) => {
  return <React.Suspense fallback={<div></div>}>{children}</React.Suspense>;
};

const RedirectExternal = ({ redirect, newTab = false }) => {
  useEffect(() => {
    if (newTab) {
      window.open(redirect, "_blank");
    } else {
      window.location.replace(redirect);
    }
  }, [redirect, newTab]);

  return newTab ? <Navigate to="/" replace /> : null; // Fallback only for newTab case
};

function App() {
  const { i18n } = useTranslation();

  const { loggedIn, setLoggedIn } = React.useContext(AuthContext);
  const [locationConfirmed, setLocationConfirmed] = React.useState(true);

  React.useEffect(() => {
    getLocale();
  }, []);

  const hostname = window.location.hostname;

  document.addEventListener("snipcart.ready", () => {
    if (localStorage.getItem("user-prefsv2") != null) {
      var user_prefs = JSON.parse(localStorage.getItem("user-prefsv2"));
      Snipcart.api.session.setLanguage(user_prefs.language);
      Snipcart.api.session.setCurrency(user_prefs.currency.code.toLowerCase());
    }
  });

  window.addEventListener("located", () => {
    var user_prefs = JSON.parse(localStorage.getItem("user-prefsv2"));
    i18n.changeLanguage(user_prefs.language);
  });

  window.addEventListener("locate_error", () => {
    setLocationConfirmed(false);
  });

  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: false } },
  });

  useEffect(() => {
    const triggerLoad = () => {
      loadFreshworks();
      window.removeEventListener("scroll", triggerLoad);
      window.removeEventListener("click", triggerLoad);
    };

    window.addEventListener("scroll", triggerLoad, { once: true });
    window.addEventListener("click", triggerLoad, { once: true });
  }, []);

  useEffect(() => {
    const triggerLoad = () => {
      initSentry();
      window.removeEventListener("scroll", triggerLoad);
      window.removeEventListener("click", triggerLoad);
    };

    window.addEventListener("scroll", triggerLoad, { once: true });
    window.addEventListener("click", triggerLoad, { once: true });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <ScrollToTop />
        <div className="fixed top-0 left-0 w-full h-full bg-background z-[-1]"></div>
        <img
          src="/assets/branding/background.webp"
          className="h-screen w-screen fixed top-0 left-0 z-[-1] opacity-5 object-cover"
          alt="background"
          loading="lazy"
        />
        <Navbar setLoggedIn={setLoggedIn} loggedIn={loggedIn} />
        <Toaster position="top-center" closeButton richColors />
        <Lazy>
          <WelcomeBox
            setLocationConfirmed={setLocationConfirmed}
            locationConfirmed={locationConfirmed}
          />
        </Lazy>
        <Routes>
          {redirects.map((item) => (
            <Route
              path={item.path}
              element={<RedirectExternal redirect={item.redirect} />}
              key={item.path}
            />
          ))}
          <Route
            path="/success"
            element={
              <Lazy>
                <Success />
              </Lazy>
            }
          />
          <Route
            path="/referrals"
            element={
              <Lazy>
                <Referrals />
              </Lazy>
            }
          />
          <Route
            path="/share/:id"
            element={
              <Lazy>
                <Share />
              </Lazy>
            }
          />
          <Route
            path="*"
            element={
              <Lazy>
                <FourZeroFour />
              </Lazy>
            }
          />

          <Route
            path="/reset"
            element={
              <Lazy>
                <ForgetPass />
              </Lazy>
            }
          />

          <Route
            path="/lost"
            element={
              <Lazy>
                <Lost />
              </Lazy>
            }
          />

          {loggedIn ? (
            <>
              <Route
                path="/add-device"
                element={
                  <Lazy>
                    <Navigate to="/activate" />
                  </Lazy>
                }
              />
              <Route
                path="/activate"
                element={
                  <Lazy>
                    <AddDevice setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/account/*"
                element={
                  <Lazy>
                    <Account setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              >
                <Route path="" element={<Navigate to="/account/general" />} />
                <Route
                  path="general"
                  element={
                    <Lazy>
                      <General />
                    </Lazy>
                  }
                />
                <Route
                  path="billing"
                  element={
                    <Lazy>
                      <Billing />
                    </Lazy>
                  }
                />
                <Route path="*" element={<Navigate to="/account" />} />
              </Route>

              <Route
                path="/my-devices"
                element={
                  <Lazy>
                    <Navigate to="/manage" />
                  </Lazy>
                }
              />
              <Route
                path="/manage"
                element={
                  <Lazy>
                    <MyDevices />
                  </Lazy>
                }
              />
              <Route
                path="/"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/login"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/mua-accept"
                element={
                  <Lazy>
                    <MUALogin setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/signup"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/confirmEmail"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/reset"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
            </>
          ) : (
            <>
              <Route
                path="/"
                element={
                  <Lazy>
                    <Login setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              {/*
              <Route
                path="/track"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/history"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              */}
              <Route
                path="/login"
                element={
                  <Lazy>
                    <Login setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/mua-accept"
                element={
                  <Lazy>
                    <MUALogin setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/signup"
                element={
                  <Lazy>
                    <Register />
                  </Lazy>
                }
              />
              <Route
                path="/confirmEmail"
                element={
                  <Lazy>
                    <CEmail />
                  </Lazy>
                }
              />
              <Route
                path="/add-device"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/activate"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/account"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/account/*"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />

              <Route
                path="/my-devices"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
              <Route
                path="/manage"
                element={
                  <Lazy>
                    <Redirect setLoggedIn={setLoggedIn} />
                  </Lazy>
                }
              />
            </>
          )}
        </Routes>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
